// campaignLogs collection — lifecycle audit for campaign operations.
// Each row records a state transition (created, started, paused,
// completed, etc.) so the campaign dashboard can reconstruct what
// happened without polling state.

import { z } from 'zod';
import { BaseEntitySchema } from './base-entity';
import { CampaignSchema } from './campaign';

export const ClientTypeList = ['gateway', 'whatsapp', 'frontend'] as const;
export const ClientTypeSchema = z.enum(ClientTypeList);
export type ClientType = z.infer<typeof ClientTypeSchema>;

export const CampaignLogStatusList = [
  'campaign-created',
  'campaign-deleted',
  'campaign-started',
  'campaign-message-created',
  'campaign-messages-generation-complete',
  'campaign-completed-sending',
  'campaign-completed',
  'campaign-paused',
  'campaign-aborted',
  'campaign-added-to-client',
  // #590 — drip (recurring sequence) lifecycle audit. Written by
  // recordDripLifecycle (services/drips/drip-audit.ts) as the durable, support-
  // queryable record of a pause / resume / skip-next-occurrence action. Keyed by
  // the top-level `dripId` below (a drip-scoped row need not carry a campaignId —
  // a pause/skip acts on the drip, not on a single step campaign).
  'drip-paused',
  'drip-resumed',
  'drip-skipped',
] as const;
export const CampaignLogStatusSchema = z.enum(CampaignLogStatusList);
export type CampaignLogStatus = z.infer<typeof CampaignLogStatusSchema>;

/** Contact info captured at the moment a campaign message was created
 *  for that contact (so the log row survives contact-doc edits / deletes). */
export const CampaignLogContactSchema = z.object({
  whatsappId: z.string().optional(),
  name: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  mobile: z.string().optional(),
  profilePicUrl: z.string().optional(),
});
export type CampaignLogContact = z.infer<typeof CampaignLogContactSchema>;

export const CampaignLogMetadataSchema = z.object({
  previousStatus: z.string().optional(),
  /** User id who initiated the action. */
  initiatedBy: z.string().optional(),
  /** Custom message describing the event. */
  logMessage: z.string().optional(),
  contactCount: z.number().int().optional(),
  messagesGenerated: z.number().int().optional(),
  messagesSent: z.number().int().optional(),
  messagesDelivered: z.number().int().optional(),
  /** mqttClientId / sessionId / etc. */
  clientId: z.string().optional(),
  /** Error payload — opaque (caller-shaped). */
  error: z.unknown().optional(),
  contact: CampaignLogContactSchema.optional(),
});
export type CampaignLogMetadata = z.infer<typeof CampaignLogMetadataSchema>;

export const CampaignLogSourceSchema = z.enum(['backend', 'client']);
export type CampaignLogSource = z.infer<typeof CampaignLogSourceSchema>;

export const CampaignLogSchema = BaseEntitySchema.extend({
  campaignId: z.union([z.string(), z.number(), z.null()]).optional(),
  /** #590 — the drip this row audits, when the row records a drip lifecycle
   *  action (drip-paused/-resumed/-skipped). Top-level (not in metadata) so the
   *  support runbook can query `campaign-logs` by dripId directly. */
  dripId: z.union([z.string(), z.number(), z.null()]).optional(),
  /** Optional campaign snapshot at the time of the log row. Schema-
   *  typed via the bt-common Campaign (#53) — `passthrough()` keeps
   *  any extra fields older log rows captured. */
  campaign: CampaignSchema.passthrough().optional(),
  owner: z.union([z.string(), z.number(), z.null()]).optional(),
  status: CampaignLogStatusSchema,
  metadata: CampaignLogMetadataSchema.optional(),
  clientType: ClientTypeSchema.optional(),
  clientId: z.string().optional(),
  source: CampaignLogSourceSchema,
  deleted: z.boolean().optional(),
  createdAt: z.union([z.date(), z.string()]).optional(),
  updatedAt: z.union([z.date(), z.string()]).optional(),
});
export type CampaignLog = z.infer<typeof CampaignLogSchema>;
